import {
  Bell,
  CreditCard,
  Database,
  FileText,
  LayoutDashboard,
  Mail,
  Settings,
  Shield,
  TrendingUp,
  Users,
} from 'lucide-react';
import { NavigationMenu } from '../layout/navigation-menu';

/**
 * Example usage of the NavigationMenu component
 *
 * This demonstrates:
 * - All items start collapsed
 * - Only one item can be expanded at a time (accordion behavior)
 * - Smooth transitions between states
 */
export function NavigationMenuExample() {
  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/dashboard',
    },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      children: [
        {
          id: 'users-all',
          label: 'All Users',
          icon: Users,
          href: '/users/all',
        },
        {
          id: 'users-roles',
          label: 'Roles & Permissions',
          icon: Shield,
          href: '/users/roles',
        },
        {
          id: 'users-activity',
          label: 'Activity Log',
          icon: Bell,
          href: '/users/activity',
        },
      ],
    },
    {
      id: 'content',
      label: 'Content',
      icon: FileText,
      children: [
        {
          id: 'content-posts',
          label: 'Posts',
          icon: FileText,
          href: '/content/posts',
        },
        {
          id: 'content-pages',
          label: 'Pages',
          icon: FileText,
          href: '/content/pages',
        },
        {
          id: 'content-media',
          label: 'Media Library',
          icon: Database,
          href: '/content/media',
        },
      ],
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard,
      children: [
        {
          id: 'billing-invoices',
          label: 'Invoices',
          icon: FileText,
          href: '/billing/invoices',
        },
        {
          id: 'billing-subscriptions',
          label: 'Subscriptions',
          icon: CreditCard,
          href: '/billing/subscriptions',
        },
        {
          id: 'billing-reports',
          label: 'Reports',
          icon: TrendingUp,
          href: '/billing/reports',
        },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      children: [
        {
          id: 'settings-general',
          label: 'General',
          icon: Settings,
          href: '/settings/general',
        },
        {
          id: 'settings-notifications',
          label: 'Notifications',
          icon: Bell,
          href: '/settings/notifications',
        },
        {
          id: 'settings-email',
          label: 'Email Templates',
          icon: Mail,
          href: '/settings/email',
        },
        {
          id: 'settings-security',
          label: 'Security',
          icon: Shield,
          href: '/settings/security',
        },
      ],
    },
  ];

  // biome-ignore lint/suspicious/noExplicitAny: pre-existing
  const handleItemClick = (item: any) => {
    console.log('Clicked item:', item);
    // Handle navigation or other actions here
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Navigation Menu Component</h1>
          <p className="mt-2 text-muted-foreground">Accordion-style navigation with mutual exclusivity</p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Example 1: Basic Navigation */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold">Basic Navigation</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Click any expandable item to see the accordion behavior. Only one section can be expanded at a time.
            </p>
            <NavigationMenu items={menuItems} onItemClick={handleItemClick} />
          </div>

          {/* Example 2: Behavior Description */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold">Key Features</h2>
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-semibold text-foreground">Initial State</h3>
                <p className="text-muted-foreground">
                  All expandable menu items start in a collapsed state when the page loads.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Accordion Behavior</h3>
                <p className="text-muted-foreground">
                  Only one menu module can be expanded at any given time. When you expand a new item, the currently
                  expanded item automatically collapses.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Smooth Transitions</h3>
                <p className="text-muted-foreground">
                  The collapse-then-expand sequence occurs smoothly with CSS transitions for clear visual feedback.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Accessibility</h3>
                <p className="text-muted-foreground">
                  Includes proper ARIA attributes (aria-expanded, aria-controls) for screen reader support.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Code Example */}
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-xl font-semibold">Usage Example</h2>
          <pre className="overflow-x-auto rounded-md bg-muted p-4 text-sm">
            <code>{`import { NavigationMenu } from './components/layout/navigation-menu';

const menuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
  },
  {
    id: 'users',
    label: 'Users',
    icon: Users,
    children: [
      {
        id: 'users-all',
        label: 'All Users',
        icon: Users,
        href: '/users/all',
      },
      // ... more children
    ],
  },
  // ... more items
];

<NavigationMenu 
  items={menuItems} 
  onItemClick={(item) => console.log(item)} 
/>`}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}
